import React from "react";
import { Link } from "react-router-dom";
import {
    Box, Heading, Text, VStack, HStack, SimpleGrid, Badge, Icon, Card, Button,
    Accordion, AccordionItem, AccordionButton, AccordionPanel, AccordionIcon
} from "@chakra-ui/react";
import { FiBook, FiCalendar, FiMapPin, FiUser } from "react-icons/fi";
import { FaCalendarPlus } from "react-icons/fa";
import moment from "moment";
import { getCertStatus, getModuleStatus, getOrdinalSuffix } from "../utils/formatHelpers";
import { generateMultipleICSContent, downloadICS } from "../utils/calendarHelpers";
import { PageHeader, getStatusBadgeColors } from "./portalUi";
import { COLORS } from "../styles/colors";

export const CoursesTab = ({ certifications, modules, isMobile, toast }) => {

    const handleDownloadAllCertificationCalendar = (certId, courseTitle) => {
        const allSessions = [];
        if (modules[certId] && modules[certId].length > 0) {
            modules[certId].forEach(module => {
                const moduleName = module.course_type?.name || module.course_type_code || "Module";
                if (module.course_dates && module.course_dates.length > 0) {
                    module.course_dates.forEach(cd => {
                        allSessions.push({
                            date: moment(cd.date).format("YYYY-MM-DD"),
                            start_time: cd.start_time, end_time: cd.end_time,
                            venue: cd.venue, venueAddress: cd.venueAddress, room: cd.room,
                            session_id: cd.date, moduleName,
                            meeting_url: cd.meeting_url || "",
                            is_online: !!cd.is_online || !!cd.meeting_url,
                        });
                    });
                }
            });
        }
        if (allSessions.length === 0) {
            toast({ title: "No Sessions", description: "This certification has no scheduled sessions yet.", status: "warning", duration: 3000, isClosable: true });
            return;
        }
        const certName = courseTitle || "Certification";
        downloadICS(generateMultipleICSContent(allSessions, certName), `${certName.replace(/\s+/g, "_")}_All_Sessions.ics`);
        if (!isMobile) {
            toast({ title: "All Calendar Events Downloaded", description: `${allSessions.length} sessions downloaded. Open the file to add all to your calendar.`, status: "success", duration: 5000, isClosable: true });
        }
    };

    return (
        <VStack spacing={{ base: 4, md: 5 }} align="stretch">
            <PageHeader
              icon={FiBook}
              iconColor="blue.500"
              title="My Courses"
              subtitle="Your enrolled programs and modules"
            />
            {certifications.length > 0 ? (
                <Accordion allowMultiple>
                    {certifications.map((cert) => {
                        const certModules = modules[cert.id] || [];
                        const certStatus = getCertStatus(cert);
                        const certBadge = getStatusBadgeColors(certStatus.label);

                        return (
                            <AccordionItem
                              key={cert.id}
                              mb={{ base: 3, md: 4 }}
                              border="1px solid"
                              borderColor="gray.200"
                              borderRadius="xl"
                              overflow="hidden"
                              bg="white"
                            >
                                {() => (
                                    <>
                                        <AccordionButton
                                            bg="white"
                                            _hover={{ bg: "gray.50" }}
                                            p={{ base: 4, md: 5 }}
                                            flexDirection={{ base: "column", md: "row" }}
                                            alignItems={{ base: "flex-start", md: "center" }}
                                        >
                                            <Box flex="1" textAlign="left" w="100%">
                                                <VStack align="flex-start" spacing={2} mb={2}>
                                                    <HStack spacing={2} flexWrap="wrap" align="center">
                                                        <Badge colorScheme="blue" fontSize="2xs" borderRadius="full" px={2.5} py={0.5} textTransform="none">
                                                          {cert.run_name}
                                                        </Badge>
                                                        <Badge
                                                          bg={certBadge.bg}
                                                          color={certBadge.color}
                                                          fontSize="2xs"
                                                          textTransform="none"
                                                          fontWeight="600"
                                                          px={2.5}
                                                          py={0.5}
                                                          borderRadius="full"
                                                        >
                                                            {certStatus.label}
                                                        </Badge>
                                                    </HStack>
                                                    <Heading
                                                      size={{ base: "sm", md: "md" }}
                                                      color={COLORS.maastrichtBlue}
                                                      fontSize={{ base: "md", md: "lg" }}
                                                      width="100%"
                                                      lineHeight="shorter"
                                                    >
                                                        {cert.full_certification?.name}
                                                    </Heading>
                                                </VStack>
                                                <Text fontSize={{ base: "xs", md: "sm" }} color="gray.500" mt={1}>
                                                    {moment(cert.start_date).format("DD MMM YYYY")} – {moment(cert.end_date).format("DD MMM YYYY")}
                                                </Text>
                                            </Box>
                                            <HStack spacing={2} mt={{ base: 2, md: 0 }} onClick={(e) => e.stopPropagation()}>
                                                <Button
                                                    size={{ base: "xs", md: "sm" }}
                                                    colorScheme="purple"
                                                    borderRadius="full"
                                                    leftIcon={<Icon as={FaCalendarPlus} />}
                                                    onClick={() => handleDownloadAllCertificationCalendar(cert.id, cert.full_certification?.name || "Certification")}
                                                    w={{ base: "100%", md: "auto" }}
                                                >
                                                    <Text display={{ base: "none", sm: "block" }}>Add All to Calendar</Text>
                                                    <Text display={{ base: "block", sm: "none" }}>Calendar</Text>
                                                </Button>
                                                <AccordionIcon />
                                            </HStack>
                                        </AccordionButton>

                                        <AccordionPanel pb={{ base: 4, md: 5 }} px={{ base: 3, md: 5 }} bg="gray.50">
                                            {certModules.length > 0 ? (
                                                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={{ base: 3, md: 4 }}>
                                                    {[...certModules]
                                                        .sort((a, b) => moment(a.start_date).diff(moment(b.start_date)))
                                                        .map((module) => {
                                                            const modStatus = getModuleStatus(module);
                                                            const modBadge = getStatusBadgeColors(modStatus.label);
                                                            const isOngoing = modStatus.label === "Ongoing";
                                                            const isCompleted = modStatus.label === "Completed";
                                                            return (
                                                                <Card
                                                                    key={module.id}
                                                                    p={{ base: 3, md: 4 }}
                                                                    borderWidth="1px"
                                                                    borderRadius="xl"
                                                                    bg="white"
                                                                    borderColor={isOngoing ? "blue.200" : "gray.200"}
                                                                    borderLeftWidth="3px"
                                                                    borderLeftColor={isOngoing ? "blue.400" : isCompleted ? "green.400" : "gray.300"}
                                                                    opacity={isCompleted ? 0.92 : 1}
                                                                    _hover={{
                                                                        borderColor: isOngoing ? "blue.300" : "gray.300",
                                                                        shadow: "md",
                                                                        transform: "translateY(-2px)",
                                                                    }}
                                                                    transition="all 0.2s"
                                                                    position="relative"
                                                                >
                                                                    <Box
                                                                        as={Link}
                                                                        to={`/courses/${module.id}`}
                                                                        onClick={(e) => e.stopPropagation()}
                                                                        position="absolute"
                                                                        top={0}
                                                                        left={0}
                                                                        right={0}
                                                                        bottom={0}
                                                                        zIndex={1}
                                                                    />
                                                                    <VStack align="stretch" spacing={{ base: 2, md: 3 }}>
                                                                        <Box>
                                                                            <Badge colorScheme="teal" mb={2} fontSize="2xs" borderRadius="full" px={2.5} textTransform="none">
                                                                                {module.id || module.course_type?.code}
                                                                            </Badge>
                                                                            <Heading size={{ base: "xs", md: "sm" }} color={COLORS.maastrichtBlue} fontSize={{ base: "sm", md: "md" }} lineHeight="shorter">
                                                                                {module.course_type?.name}
                                                                            </Heading>
                                                                        </Box>

                                                                        <HStack spacing={2} color="gray.600" flexWrap="wrap" align="center">
                                                                            <Icon as={FiCalendar} boxSize={4} flexShrink={0} />
                                                                            <Text fontSize={{ base: "xs", md: "sm" }}>
                                                                                {moment(module.start_date).format("DD MMM")} - {moment(module.end_date).format("DD MMM YYYY")}
                                                                            </Text>
                                                                            {modStatus.label !== "\u2014" && (
                                                                                <Badge
                                                                                  bg={modBadge.bg}
                                                                                  color={modBadge.color}
                                                                                  fontSize="2xs"
                                                                                  px={2.5}
                                                                                  py={0.5}
                                                                                  fontWeight="600"
                                                                                  textTransform="none"
                                                                                  borderRadius="full"
                                                                                >
                                                                                    {modStatus.label}
                                                                                </Badge>
                                                                            )}
                                                                        </HStack>

                                                                        {(() => {
                                                                            const venueName = module.venues?.name || (module.course_dates?.length > 0 && module.course_dates[0].venue) || module.venue;
                                                                            const roomName = module.rooms?.name || (module.course_dates?.length > 0 && module.course_dates[0].room);
                                                                            const formatFloorUnit = () => {
                                                                                if (!module.rooms?.floor && !module.rooms?.unit) return "";
                                                                                const parts = [];
                                                                                if (module.rooms.floor) {
                                                                                    const f = module.rooms.floor;
                                                                                    parts.push(isNaN(f) ? f : `${f}${getOrdinalSuffix(Number(f))} Floor`);
                                                                                }
                                                                                if (module.rooms.unit) parts.push(`Unit ${module.rooms.unit}`);
                                                                                return parts.length > 0 ? ` (${parts.join(", ")})` : "";
                                                                            };
                                                                            if (venueName && venueName !== "TBC" && venueName !== "TBD") {
                                                                                return (
                                                                                    <HStack spacing={2} color="gray.600" align="center" flexWrap="nowrap">
                                                                                        <Icon as={FiMapPin} boxSize={4} flexShrink={0} />
                                                                                        <Text fontSize={{ base: "xs", md: "sm" }} flex="1" minW={0}>
                                                                                            {venueName}{roomName && roomName !== "TBC" && roomName !== "TBD" && ` - ${roomName}`}{formatFloorUnit()}
                                                                                        </Text>
                                                                                    </HStack>
                                                                                );
                                                                            }
                                                                            return null;
                                                                        })()}

                                                                        {module.trainers && (
                                                                            <HStack spacing={2} color="gray.600" flexWrap="wrap">
                                                                                <Icon as={FiUser} boxSize={4} />
                                                                                <Text fontSize={{ base: "xs", md: "sm" }}>{module.trainers.name}</Text>
                                                                            </HStack>
                                                                        )}

                                                                        {module.course_dates?.length > 0 && (
                                                                            <Text fontSize={{ base: "2xs", md: "xs" }} color="purple.600" fontWeight="600">
                                                                                {module.course_dates.length} session{module.course_dates.length !== 1 ? "s" : ""} scheduled
                                                                            </Text>
                                                                        )}
                                                                    </VStack>
                                                                </Card>
                                                            );
                                                        })}
                                                </SimpleGrid>
                                            ) : (
                                                <Box p={4} textAlign="center">
                                                    <Text color="gray.500" fontSize="sm">No modules assigned yet.</Text>
                                                </Box>
                                            )}
                                        </AccordionPanel>
                                    </>
                                )}
                            </AccordionItem>
                        );
                    })}
                </Accordion>
            ) : (
                <Box textAlign="center" py={8} borderRadius="xl" border="1px solid" borderColor="gray.200" bg="white">
                    <Text color="gray.500" fontSize="md">No courses enrolled yet.</Text>
                </Box>
            )}
        </VStack>
    );
};
